import { useState } from "react";
import { Building2, MapPin, MessageCircle, ShieldCheck } from "lucide-react";
import { SITE, waLink, buildEnquiryMessage, WA_MESSAGES } from "@/config/site";
import { LOCATIONS, CARE_TYPES, DURATIONS } from "@/data/content";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import Reveal from "@/components/Reveal";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import SevenSteps from "@/components/SevenSteps";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-brand-charcoal focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/20 transition";

const Field = ({ label, htmlFor, children, optional }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
      {label} {optional && <span className="font-medium normal-case tracking-normal text-slate-400">(optional)</span>}
    </label>
    {children}
  </div>
);

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", phone: "", area: "", careType: "", duration: "", budget: "", date: "", message: "" });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    window.open(waLink(buildEnquiryMessage(form)), "_blank", "noopener");
  };

  return (
    <main data-testid="page-contact">
      <Seo
        title="Contact & Free Care Matching | Nursing Home in Penang"
        description="Contact Nursing Home in Penang for free nursing home matching assistance. Share your location, care requirements and budget via WhatsApp and we will help you explore suitable options."
        path="/contact/"
      />

      <section className="relative overflow-hidden bg-brand-teal text-white grain">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand-coral/20 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <Breadcrumbs items={[{ label: "Contact & Free Care Matching" }]} />
          <Reveal className="mt-8 max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-gold">Free for Families</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
              Contact Us & Free Care Matching
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed">
              Finding the right nursing home for your loved one can be overwhelming. We help families explore suitable
              care options based on preferred location, care requirements, budget and admission timeline — free of
              charge.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <form data-testid="contact-form" onSubmit={onSubmit} className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-10 shadow-[0_20px_60px_rgb(7,91,89,0.08)] space-y-5">
              <h2 className="font-display text-2xl font-semibold text-brand-charcoal">Send Your Enquiry</h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                Enquiries are handled through WhatsApp. Submitting this form opens a WhatsApp chat with your details
                pre-filled — nothing is sent until you press send in WhatsApp.
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Contact Name" htmlFor="contact-name">
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                </Field>
                <Field label="Phone / WhatsApp Number" htmlFor="contact-phone">
                  <input id="contact-phone" data-testid="contact-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="e.g. 012-345 6789" />
                </Field>
                <Field label="Preferred Penang Area" htmlFor="contact-area">
                  <select id="contact-area" data-testid="contact-area-select" value={form.area} onChange={set("area")} className={inputCls}>
                    <option value="">Select an area</option>
                    {LOCATIONS.map((l) => (<option key={l} value={l}>{l}</option>))}
                    <option value="Anywhere in Penang">Anywhere in Penang</option>
                  </select>
                </Field>
                <Field label="Type of Care Required" htmlFor="contact-care-type">
                  <select id="contact-care-type" data-testid="contact-care-type-select" value={form.careType} onChange={set("careType")} className={inputCls}>
                    <option value="">Select care type</option>
                    {CARE_TYPES.map((c) => (<option key={c.value} value={c.label}>{c.label}</option>))}
                  </select>
                </Field>
                <Field label="Preferred Duration" htmlFor="contact-duration">
                  <select id="contact-duration" data-testid="contact-duration-select" value={form.duration} onChange={set("duration")} className={inputCls}>
                    <option value="">Select duration</option>
                    {DURATIONS.map((d) => (<option key={d.value} value={d.label}>{d.label}</option>))}
                  </select>
                </Field>
                <Field label="Monthly Budget (RM)" htmlFor="contact-budget" optional>
                  <input id="contact-budget" data-testid="contact-budget-input" value={form.budget} onChange={set("budget")} className={inputCls} placeholder="e.g. 2500" inputMode="numeric" />
                </Field>
                <Field label="Expected Admission Date" htmlFor="contact-date" optional>
                  <input id="contact-date" data-testid="contact-date-input" type="date" value={form.date} onChange={set("date")} className={inputCls} />
                </Field>
              </div>
              <Field label="Message" htmlFor="contact-message" optional>
                <textarea id="contact-message" data-testid="contact-message-input" rows="4" value={form.message} onChange={set("message")} className={inputCls} placeholder="Anything else we should know about your family member's needs?" />
              </Field>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-teal px-7 py-4 text-base font-semibold text-white shadow-[0_8px_30px_rgb(7,91,89,0.25)] hover:bg-brand-gold hover:-translate-y-0.5 transition-all duration-300"
              >
                <MessageCircle className="h-5 w-5" /> Continue on WhatsApp
              </button>
              <p className="flex gap-2 text-xs text-slate-400 leading-relaxed">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-sage" />
                We collect only the information necessary to identify suitable options and use it solely to respond to
                your enquiry. Please do not send sensitive medical records through this form or WhatsApp.
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl bg-brand-teal p-6 sm:p-8 text-white grain relative overflow-hidden">
              <h2 className="font-display text-xl font-semibold">Company Details</h2>
              <p className="mt-3 text-sm font-semibold text-brand-gold">Brand: NursingHomeInPenang.com</p>
              <p className="mt-1 text-sm text-white/80 leading-relaxed">
                Website identity: A nursing home recommendation and matching service in Penang.
              </p>
              <ul className="mt-4 space-y-4 text-sm text-white/80 border-t border-white/15 pt-4">
                <li className="flex gap-2.5">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                  <span>{SITE.company.name}<br /><span className="text-white/50">Reg. No. {SITE.company.registration}</span></span>
                </li>
                <li className="flex gap-2.5">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                  <span>{SITE.company.address}</span>
                </li>
                <li className="flex gap-2.5">
                  <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                  <span>WhatsApp {SITE.whatsappDisplay}</span>
                </li>
              </ul>
              <WhatsAppCTA testid="contact-direct-whatsapp" variant="white" label="Chat With Us on WhatsApp" className="mt-6 w-full" />
            </div>
            <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
              <iframe
                title="Map — Nursing Home in Penang Agency, Mansion One, Jalan Sultan Ahmad Shah, Penang"
                data-testid="contact-map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d8149415.711906171!2d103.87133134103135!3d4.179692425590319!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x304ac398f1647ef7%3A0xccd9bbe6983009f1!2sNursing%20Home%20in%20Penang%20Agency!5e0!3m2!1sen!2smy!4v1791644163157!5m2!1sen!2smy"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
              <p className="px-6 py-4 text-xs text-slate-500">
                Nursing Home in Penang Agency · {SITE.company.name} · {SITE.company.address}
              </p>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
              <h2 className="font-display text-xl font-semibold text-brand-charcoal">How Enquiries Are Handled</h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Our nursing home matching and coordination assistance is free for families. We help you communicate with
                potential care providers, arrange viewing appointments and understand the admission process.
              </p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                The selected nursing home or care facility will charge its own accommodation and care fees. Families
                should confirm all charges, deposits, services and payment terms directly with the facility before
                admission.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-teal">The Full Process</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-brand-charcoal">
              The Eight-Step Nursing Home Arrangement Process
            </h2>
          </Reveal>
          <div className="mt-10">
            <SevenSteps detailed />
          </div>
          <Reveal className="mt-12 text-center">
            <h3 className="font-display text-2xl font-semibold text-brand-charcoal">Ready to Find a Suitable Nursing Home in Penang?</h3>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              Tell us your preferred location, care requirements and budget. We will help you explore available options
              and coordinate the next steps.
            </p>
            <WhatsAppCTA testid="contact-steps-cta" message={WA_MESSAGES.freeAssistance} className="mt-6" />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
